import { OfflineResponse } from './offlineDatabase';
import { legacyNegativeId, safeNegativeId } from '../utils/idUtils';

export interface OfflineOnDemandSyncItem {
  id?: number | string | null;
  temp_id: string;
  review_id: number;
  name: string;
  description?: string | null;
  category_id?: number | string | null;
  questions_schema?: any;
  synced_at?: string | Date | null;
}

interface OfflineReviewRecord {
  id?: number;
  tempId?: string | null;
}

export interface OfflineOnDemandSyncDatabase {
  getResponsesByReviewId(reviewId: number): Promise<OfflineResponse[]>;
  updateOfflineItemSyncStatus(
    tempId: string,
    status: 'pending' | 'syncing' | 'synced' | 'failed',
    syncError?: string,
    realId?: number
  ): Promise<void>;
  getReview(reviewId: number): Promise<OfflineReviewRecord | null | undefined>;
  saveIdMapping(tempId: string, realId: number, entityType: 'item'): Promise<void>;
  executeSql(sql: string, params?: any[]): Promise<any>;
}

export interface OfflineOnDemandSyncServiceDatabase extends OfflineOnDemandSyncDatabase {
  getPendingOfflineOnDemandItems(): Promise<OfflineOnDemandSyncItem[]>;
}

export interface OfflineOnDemandReviewApi {
  getOnDemandItems(reviewId: number): Promise<any[]>;
  createOnDemandItem(reviewId: number, data: any, options?: { forceOnline?: boolean }): Promise<any>;
  updateOnDemandItem(
    reviewId: number,
    itemId: number,
    data: any,
    options?: { forceOnline?: boolean }
  ): Promise<any>;
}

export interface SyncOfflineOnDemandItemOptions {
  reviewId: number;
  item: OfflineOnDemandSyncItem;
  database: OfflineOnDemandSyncDatabase;
  reviewApi: OfflineOnDemandReviewApi;
  getRealId(tempId: string): Promise<number | undefined>;
  mapTempIdToRealId(tempId: string, realId: number, entityType: 'item'): Promise<void>;
  syncSingleResponse(
    serverReviewId: number,
    response: OfflineResponse,
    resolvedItemId?: number | null
  ): Promise<boolean>;
  recomputeReviewSyncStatus(reviewId: number): Promise<void>;
  logger?: Pick<Console, 'error'>;
}

export interface OfflineOnDemandItemSyncServiceDependencies {
  database: OfflineOnDemandSyncServiceDatabase;
  reviewApi: OfflineOnDemandReviewApi;
  getRealId(tempId: string): Promise<number | undefined>;
  mapTempIdToRealId(tempId: string, realId: number, entityType: 'item'): Promise<void>;
  syncSingleResponse(
    serverReviewId: number,
    response: OfflineResponse,
    resolvedItemId?: number | null
  ): Promise<boolean>;
  recomputeReviewSyncStatus(reviewId: number): Promise<void>;
  syncItem?: (options: SyncOfflineOnDemandItemOptions) => Promise<void>;
}

export function isSameOnDemandItem(serverItem: any, offlineItem: any): boolean {
  const normalizeText = (value: unknown) => (value ?? '').toString().trim();
  const serverCategoryId = serverItem.categoryId ?? serverItem.category_id ?? null;
  const offlineCategoryId = offlineItem.categoryId ?? offlineItem.category_id ?? null;

  return (
    normalizeText(serverItem.name) === normalizeText(offlineItem.name) &&
    normalizeText(serverItem.description) === normalizeText(offlineItem.description) &&
    (serverCategoryId ?? null) === (offlineCategoryId ?? null)
  );
}

function uniqueFiniteIds(ids: Array<number | undefined | null>): number[] {
  return Array.from(new Set(
    ids.filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
  ));
}

function getOfflineItemReferenceIds(item: Pick<OfflineOnDemandSyncItem, 'id' | 'temp_id'>): number[] {
  const rowId = Number(item.id);
  return uniqueFiniteIds([
    Number.isFinite(rowId) ? rowId : null,
    item.temp_id ? safeNegativeId(item.temp_id) : null,
    item.temp_id ? legacyNegativeId(item.temp_id) : null,
  ]);
}

function resolveSyncedItemId(syncedItem: any): number {
  const syncedItemId = Number(syncedItem?.id);

  if (!Number.isFinite(syncedItemId) || syncedItemId <= 0) {
    throw new Error(`On-demand item sync did not receive a server item id: ${syncedItem?.id ?? 'missing'}`);
  }

  return syncedItemId;
}

function buildOnDemandItemSyncPayload(item: OfflineOnDemandSyncItem): any {
  const payload: any = {
    name: item.name,
    description: item.description,
    categoryId: item.category_id,
  };

  if (item.questions_schema != null) {
    payload.questionsSchema = item.questions_schema;
  }

  return payload;
}

export class OfflineOnDemandItemSyncService {
  private readonly syncItem: (options: SyncOfflineOnDemandItemOptions) => Promise<void>;

  constructor(private readonly dependencies: OfflineOnDemandItemSyncServiceDependencies) {
    this.syncItem = dependencies.syncItem ?? syncOfflineOnDemandItem;
  }

  async syncPendingOnDemandItems(): Promise<void> {
    const { database } = this.dependencies;
    const pendingItems = await database.getPendingOfflineOnDemandItems();

    for (const item of pendingItems) {
      await this.syncItem({
        reviewId: item.review_id,
        item,
        database,
        reviewApi: this.dependencies.reviewApi,
        getRealId: tempId => this.dependencies.getRealId(tempId),
        mapTempIdToRealId: (tempId, realId, entityType) =>
          this.dependencies.mapTempIdToRealId(tempId, realId, entityType),
        syncSingleResponse: (serverReviewId, response, resolvedItemId) =>
          this.dependencies.syncSingleResponse(serverReviewId, response, resolvedItemId),
        recomputeReviewSyncStatus: reviewId =>
          this.dependencies.recomputeReviewSyncStatus(reviewId),
      });
    }
  }
}

export async function syncOfflineOnDemandItem({
  reviewId,
  item,
  database,
  reviewApi,
  getRealId,
  mapTempIdToRealId,
  syncSingleResponse,
  recomputeReviewSyncStatus,
  logger = console,
}: SyncOfflineOnDemandItemOptions): Promise<void> {
  try {
    const localItemIds = getOfflineItemReferenceIds(item);
    const localResponsesForItem = localItemIds.length > 0
      ? (await database.getResponsesByReviewId(reviewId).catch(() => []))
        .filter(response => localItemIds.includes(Number(response.itemId)))
      : [];

    await database.updateOfflineItemSyncStatus(item.temp_id, 'syncing');

    let serverReviewId = reviewId;
    const offlineReview = await database.getReview(reviewId).catch(() => null);
    if (offlineReview?.tempId) {
      const mappedReviewId = await getRealId(offlineReview.tempId);
      if (mappedReviewId != null) {
        serverReviewId = mappedReviewId;
      } else {
        throw new Error(`Review ${reviewId} not synced yet, deferring on-demand item sync`);
      }
    }

    const isUpdate = item.temp_id?.startsWith('synced_') || item.synced_at;
    let syncedItem: any;

    if (isUpdate) {
      const serverId = parseInt(item.temp_id.replace('synced_', ''), 10);

      syncedItem = await reviewApi.updateOnDemandItem(serverReviewId, serverId, {
        ...buildOnDemandItemSyncPayload(item),
      }, { forceOnline: true });

      syncedItem.id = serverId;
    } else {
      const existingItems = await reviewApi.getOnDemandItems(serverReviewId).catch(() => []);
      const existingItem = existingItems.find((serverItem: any) =>
        isSameOnDemandItem(serverItem, item)
      );

      syncedItem = existingItem || await reviewApi.createOnDemandItem(
        serverReviewId,
        buildOnDemandItemSyncPayload(item),
        { forceOnline: true }
      );
    }

    const syncedItemId = resolveSyncedItemId(syncedItem);

    await database.saveIdMapping(item.temp_id, syncedItemId, 'item');
    await mapTempIdToRealId(item.temp_id, syncedItemId, 'item');

    if (localItemIds.length > 0) {
      try {
        for (const localItemId of localItemIds) {
          if (localItemId === syncedItemId) {
            continue;
          }

          for (const responseReviewId of uniqueFiniteIds([reviewId, offlineReview?.id, serverReviewId])) {
            await database.executeSql(
              'UPDATE responses SET item_id = ?, sync_status = ? WHERE item_id = ? AND review_id = ?',
              [syncedItemId, 'pending', localItemId, responseReviewId]
            );
          }
        }
      } catch (updateError) {
        logger.error('[OfflineReviewService] Error updating response item_id:', updateError);
      }
    }

    for (const response of localResponsesForItem) {
      const didSync = await syncSingleResponse(
        serverReviewId,
        {
          ...response,
          itemId: syncedItemId,
        },
        syncedItemId
      );

      if (didSync) {
        await database.executeSql(
          'UPDATE responses SET item_id = ?, sync_status = ?, sync_error = NULL WHERE id = ?',
          [syncedItemId, 'synced', response.id]
        );
      }
    }

    await database.updateOfflineItemSyncStatus(
      item.temp_id,
      'synced',
      undefined,
      syncedItemId
    );

    await recomputeReviewSyncStatus(reviewId);
  } catch (error) {
    logger.error('[OfflineReviewService] Failed to sync on-demand item:', {
      tempId: item.temp_id,
      name: item.name,
      reviewId,
      error: error instanceof Error ? error.message : 'Unknown error',
      errorStack: error instanceof Error ? error.stack : undefined,
    });
    await database.updateOfflineItemSyncStatus(
      item.temp_id,
      'failed',
      error instanceof Error ? error.message : 'Unknown error'
    );
  }
}
